import React, { useState, useEffect } from 'react';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WeekSelector } from "@/pages/schedule-alerts/components/WeekSelector";
import { MultiSelect } from "@/components/ui/multi-select";
import { useSchedules } from "@/pages/scheduling/hooks/useSchedules";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { Mail, Calendar, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface SendScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTeamId: string | null;
}

interface ShiftOption {
  label: string;
  value: string;
}

export const SendScheduleDialog: React.FC<SendScheduleDialogProps> = ({
  open,
  onOpenChange,
  selectedTeamId
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [selectedWeeks, setSelectedWeeks] = useState<Date[]>([new Date()]);
  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [shiftOptions, setShiftOptions] = useState<ShiftOption[]>([]);
  const [step, setStep] = useState<'weeks' | 'shifts'>('weeks');

  const { data: schedules } = useSchedules(selectedTeamId);

  // Fetch employee profiles for selected shifts
  const { data: employeeProfiles } = useQuery({
    queryKey: ['employee-profiles', selectedShifts, schedules],
    queryFn: async () => {
      if (!schedules || selectedShifts.length === 0) return [];
      
      const guardIds = Array.from(new Set(
        schedules
          .filter(schedule => selectedShifts.includes(schedule.id))
          .flatMap(schedule => schedule.assigned_guards || [])
      ));

      if (guardIds.length === 0) return [];

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .in('id', guardIds);

      if (error) {
        console.error('Error fetching employee profiles:', error);
        return [];
      }

      return data || [];
    },
    enabled: schedules && selectedShifts.length > 0
  });

  // Generate shift options based on selected weeks
  useEffect(() => {
    if (!schedules || !selectedWeeks || selectedWeeks.length === 0) {
      setShiftOptions([]);
      return;
    }

    let allShifts: typeof schedules = [];
    
    selectedWeeks.forEach(selectedWeek => {
      const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });

      const weekShifts = schedules.filter(schedule => {
        const scheduleStart = fromLocalISOString(schedule.start_date);
        const scheduleEnd = fromLocalISOString(schedule.end_date);
        
        return (
          (scheduleStart >= weekStart && scheduleStart <= weekEnd) ||
          (scheduleEnd >= weekStart && scheduleEnd <= weekEnd) ||
          (scheduleStart <= weekStart && scheduleEnd >= weekEnd)
        );
      });
      
      allShifts = [...allShifts, ...weekShifts];
    });

    // Remove duplicates
    const uniqueShifts = allShifts.filter((shift, index, self) => 
      index === self.findIndex(s => s.id === shift.id)
    );

    const options: ShiftOption[] = uniqueShifts.map(shift => ({
      label: `${shift.title} - ${format(fromLocalISOString(shift.start_date), 'MMM dd, HH:mm')} to ${format(fromLocalISOString(shift.end_date), 'MMM dd, HH:mm')}`,
      value: shift.id
    }));

    setShiftOptions(options);
    // Initially select all shifts
    setSelectedShifts(options.map(opt => opt.value));
  }, [schedules, selectedWeeks, step]);

  const handleWeekNext = () => {
    if (shiftOptions.length === 0) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: "No shifts found for the selected week",
      });
      return;
    }
    setStep('shifts');
  };

  const handleBack = () => {
    setStep('weeks');
  };

  const handleSend = async () => {
    if (!selectedTeamId) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("selectTeamFirst"),
      });
      return;
    }

    if (selectedShifts.length === 0) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: "Please select at least one shift to send",
      });
      return;
    }

    setIsSending(true);
    try {
    const { data, error } = await supabase.functions.invoke('send-weekly-schedule', {
      body: {
        teamId: selectedTeamId,
        selectedWeeks: selectedWeeks.map(week => week.toISOString()),
        shiftIds: selectedShifts
      }
    });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: t("success"),
        description: `Schedule sent successfully to ${data.emailsSent} employees`,
      });
      
      onOpenChange(false);
      setStep('weeks');
      setSelectedShifts([]);
    } catch (error) {
      console.error('Error sending schedule:', error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: "Failed to send schedule",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep('weeks');
    setSelectedShifts([]);
  };

  const addWeek = () => {
    const lastWeek = selectedWeeks[selectedWeeks.length - 1];
    const nextWeek = new Date(lastWeek);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setSelectedWeeks([...selectedWeeks, nextWeek]);
  };

  const removeWeek = (index: number) => {
    if (selectedWeeks.length > 1) {
      setSelectedWeeks(selectedWeeks.filter((_, i) => i !== index));
    }
  };

  const updateWeek = (index: number, newWeek: Date) => {
    const newWeeks = [...selectedWeeks];
    newWeeks[index] = newWeek;
    setSelectedWeeks(newWeeks);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl w-[80vw] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Schedule
          </DialogTitle>
          <DialogDescription>
            {step === 'weeks' 
              ? "Select the weeks you want to send the schedule for"
              : "Choose which shifts to include in the schedule email"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {step === 'weeks' ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Select weeks ({selectedWeeks.length} week{selectedWeeks.length !== 1 ? 's' : ''})
              </div>
              
              {selectedWeeks.map((week, index) => (
                <div key={index} className="flex items-center gap-2">
                  <WeekSelector
                    selectedWeek={week}
                    onSelectWeek={(newWeek) => updateWeek(index, newWeek)}
                  />
                  {selectedWeeks.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeWeek(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              
              <Button variant="outline" onClick={addWeek} className="w-fit">
                Add Another Week
              </Button>
              
              <div className="text-sm text-muted-foreground">
                {selectedWeeks.length === 1 ? (
                  `Week: ${format(startOfWeek(selectedWeeks[0], { weekStartsOn: 1 }), 'MMM dd')} - ${format(endOfWeek(selectedWeeks[0], { weekStartsOn: 1 }), 'MMM dd, yyyy')}`
                ) : (
                  `Period: ${format(startOfWeek(selectedWeeks[0], { weekStartsOn: 1 }), 'MMM dd')} - ${format(endOfWeek(selectedWeeks[selectedWeeks.length - 1], { weekStartsOn: 1 }), 'MMM dd, yyyy')}`
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                Select shifts ({selectedShifts.length} of {shiftOptions.length} selected)
              </div>
              <div className="flex-1 min-h-0 flex gap-4">
                {/* Shifts Selection - 2/3 of space */}
                <div className="flex-[2] overflow-y-auto border rounded-md p-4">
                  <div className="space-y-2">
                    {shiftOptions.map((shift) => {
                      const isSelected = selectedShifts.includes(shift.value);
                      return (
                        <div
                          key={shift.value}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedShifts(selectedShifts.filter(id => id !== shift.value));
                            } else {
                              setSelectedShifts([...selectedShifts, shift.value]);
                            }
                          }}
                          className={`
                            p-3 rounded-md border cursor-pointer transition-colors text-left
                            ${isSelected 
                              ? 'bg-primary text-primary-foreground border-primary' 
                              : 'bg-background hover:bg-muted border-border'
                            }
                          `}
                        >
                          <div className="font-medium text-sm">{shift.label}</div>
                        </div>
                      );
                    })}
                    {shiftOptions.length === 0 && (
                      <div className="text-sm text-muted-foreground text-center py-8">
                        No shifts found for the selected week
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Employees to be notified - 1/3 of space */}
                <div className="flex-[1] border rounded-md p-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground mb-3">
                      Employees to be notified:
                    </h4>
                    <div className="space-y-2 text-sm">
                      {employeeProfiles && employeeProfiles.length > 0 ? (
                        employeeProfiles.map((profile) => (
                          <div key={profile.id} className="p-2 bg-muted rounded text-center">
                            {profile.full_name || 
                             `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 
                             'Unknown Employee'}
                          </div>
                        ))
                      ) : selectedShifts.length > 0 ? (
                        <div className="text-muted-foreground text-center py-4">
                          Loading employee names...
                        </div>
                      ) : (
                        <div className="text-muted-foreground text-center py-4">
                          Select shifts to see employees
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          {step === 'shifts' && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          {step === 'weeks' ? (
            <Button onClick={handleWeekNext} disabled={shiftOptions.length === 0}>
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleSend} 
              disabled={isSending || selectedShifts.length === 0}
            >
              {isSending ? "Sending..." : "Send Schedule"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};